import { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Clock } from "lucide-react";
import { useTranslation } from "react-i18next";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

/**
 * Parse 24h time string → { hour, minute, second? } or null.
 * Accepts HH:mm and HH:mm:ss.
 */
function parseTime(value, withSeconds = false) {
    if (value == null || String(value).trim() === "") return null;
    const s = String(value).trim();
    const match = s.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;

    const hour = Number(match[1]);
    const minute = Number(match[2]);
    const second = match[3] != null ? Number(match[3]) : 0;

    if (
        !Number.isInteger(hour) ||
        !Number.isInteger(minute) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59
    ) {
        return null;
    }
    if (withSeconds && (second < 0 || second > 59 || !Number.isInteger(second))) {
        return null;
    }

    return { hour, minute, second };
}

function formatTimeParts({ hour, minute, second }, withSeconds = false) {
    const hh = String(hour).padStart(2, "0");
    const mm = String(minute).padStart(2, "0");
    if (withSeconds) {
        const ss = String(second ?? 0).padStart(2, "0");
        return `${hh}:${mm}:${ss}`;
    }
    return `${hh}:${mm}`;
}

function timeToMinutes({ hour, minute }) {
    return hour * 60 + minute;
}

function parseMinMax(value) {
    return parseTime(value, false);
}

function isTimeInRange(parts, min, max) {
    if (!parts) return true;
    const t = timeToMinutes(parts);
    if (min) {
        const minParts = parseMinMax(min);
        if (minParts && t < timeToMinutes(minParts)) return false;
    }
    if (max) {
        const maxParts = parseMinMax(max);
        if (maxParts && t > timeToMinutes(maxParts)) return false;
    }
    return true;
}

/**
 * 24-hour time picker (same UX patterns as Calendar).
 *
 * @param {Object} props
 * @param {string} props.value - "HH:mm" or "HH:mm:ss" when includeSeconds
 * @param {Function} props.onChange
 * @param {boolean} [props.includeSeconds=false]
 * @param {string} [props.placeholder]
 * @param {string} [props.id]
 * @param {string} [props.name]
 * @param {string} [props.className]
 * @param {boolean} [props.disabled]
 * @param {string} [props.min] - "HH:mm" minimum
 * @param {string} [props.max] - "HH:mm" maximum
 * @param {boolean} [props.required]
 * @param {number} [props.minuteStep=1] - 1, 5, 10, 15…
 */
export default function TimeComponent({
    value = "",
    onChange,
    includeSeconds = false,
    placeholder = "HH:mm",
    id = "",
    name = "",
    className = "",
    disabled = false,
    min = "",
    max = "",
    required = false,
    minuteStep = 1,
}) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [isValid, setIsValid] = useState(true);
    const [hasBlurred, setHasBlurred] = useState(false);
    const [pickerHour, setPickerHour] = useState(0);
    const [pickerMinute, setPickerMinute] = useState(0);
    const [pickerSecond, setPickerSecond] = useState(0);

    const panelRef = useRef(null);
    const inputRef = useRef(null);
    const isInternalChangeRef = useRef(false);
    const [dropdownRect, setDropdownRect] = useState(null);

    const minuteOptions = useMemo(() => {
        const step = Math.min(60, Math.max(1, Number(minuteStep) || 1));
        const list = [];
        for (let m = 0; m < 60; m += step) {
            list.push(m);
        }
        if (list[list.length - 1] !== 59 && step > 1) {
            list.push(59);
        }
        return list;
    }, [minuteStep]);

    const selectedParts = useMemo(() => parseTime(value, includeSeconds), [value, includeSeconds]);

    const updateDropdownPosition = useCallback(() => {
        if (!isOpen) return;
        const el = inputRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const viewportW = window.innerWidth || 0;
        const viewportH = window.innerHeight || 0;
        const gap = 8;
        const estimatedDropdownH = includeSeconds ? 320 : 280;
        const preferredMinWidth = Math.max(260, rect.width);

        const left = Math.max(gap, Math.min(rect.left, viewportW - preferredMinWidth - gap));

        let top = rect.bottom + gap;
        if (top + estimatedDropdownH > viewportH - gap) {
            top = Math.max(gap, rect.top - estimatedDropdownH - gap);
        }

        setDropdownRect({
            top,
            left,
            minWidth: preferredMinWidth,
        });
    }, [isOpen, includeSeconds]);

    useEffect(() => {
        if (isInternalChangeRef.current) {
            isInternalChangeRef.current = false;
            return;
        }
        if (value) {
            const parsed = parseTime(value, includeSeconds);
            if (parsed) {
                setInputValue(formatTimeParts(parsed, includeSeconds));
                setIsValid(true);
            } else {
                setInputValue(value);
            }
        } else {
            setInputValue("");
            setIsValid(true);
        }
    }, [value, includeSeconds]);

    useEffect(() => {
        if (!isOpen) return;
        const parsed = parseTime(inputValue, includeSeconds) ?? selectedParts;
        if (parsed) {
            setPickerHour(parsed.hour);
            setPickerMinute(parsed.minute);
            setPickerSecond(parsed.second ?? 0);
        } else {
            const now = new Date();
            setPickerHour(now.getHours());
            setPickerMinute(now.getMinutes());
            setPickerSecond(now.getSeconds());
        }
    }, [isOpen, inputValue, includeSeconds, selectedParts]);

    useLayoutEffect(() => {
        if (!isOpen || disabled) {
            setDropdownRect(null);
            return;
        }
        updateDropdownPosition();
    }, [isOpen, disabled, updateDropdownPosition]);

    useEffect(() => {
        if (!isOpen) return;
        const update = () => updateDropdownPosition();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [isOpen, updateDropdownPosition]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                panelRef.current &&
                !panelRef.current.contains(event.target) &&
                inputRef.current &&
                !inputRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const validateInput = (text) => {
        if (!text || text.trim() === "") {
            return { isValid: true, parts: null };
        }
        const parts = parseTime(text, includeSeconds);
        if (!parts) return { isValid: false, parts: null };
        if (!isTimeInRange(parts, min, max)) {
            return { isValid: false, parts: null };
        }
        return { isValid: true, parts };
    };

    const commitTime = (parts) => {
        if (!parts || !isTimeInRange(parts, min, max)) return;
        const formatted = formatTimeParts(parts, includeSeconds);
        isInternalChangeRef.current = true;
        setInputValue(formatted);
        setIsValid(true);
        if (onChange) onChange(formatted);
        setIsOpen(false);
    };

    const handlePickerApply = () => {
        const parts = {
            hour: pickerHour,
            minute: pickerMinute,
            second: pickerSecond,
        };
        commitTime(parts);
    };

    const handleNow = () => {
        const now = new Date();
        commitTime({
            hour: now.getHours(),
            minute: now.getMinutes(),
            second: now.getSeconds(),
        });
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
        setHasBlurred(false);
        setIsValid(true);
    };

    const handleInputBlur = () => {
        setHasBlurred(true);
        const validation = validateInput(inputValue);
        if (validation.isValid && validation.parts) {
            const formatted = formatTimeParts(validation.parts, includeSeconds);
            isInternalChangeRef.current = true;
            setInputValue(formatted);
            setIsValid(true);
            if (onChange) onChange(formatted);
        } else if (inputValue.trim() === "") {
            isInternalChangeRef.current = true;
            setIsValid(true);
            if (onChange) onChange("");
        } else {
            setIsValid(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            e.preventDefault();
            setIsOpen(false);
            inputRef.current?.blur();
        }
        if (e.key === "Escape") {
            setIsOpen(false);
        }
    };

    const isHourDisabled = (hour) => {
        for (const minute of [0, 59]) {
            if (isTimeInRange({ hour, minute, second: 0 }, min, max)) return false;
        }
        return true;
    };

    const isMinuteDisabled = (minute) => {
        return !isTimeInRange({ hour: pickerHour, minute, second: 0 }, min, max);
    };

    const pickerBtnClass = (selected) =>
        `min-h-[40px] sm:min-h-[36px] w-full rounded-lg text-sm font-medium transition-all duration-150 touch-manipulation ${selected
            ? "bg-[#84B044] text-white shadow-sm"
            : "text-gray-700 hover:bg-gray-100 active:bg-gray-200"
        }`;

    const labelMinutos = t("label_minutos") || "Minutos";
    const labelSegundos = t("label_segundos") || "Segundos";
    const invalidMsg = t("alert_hora_invalida") || "Hora inválida (use formato 24h HH:mm)";

    return (
        <div className={`relative w-full ${className}`}>
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    inputMode="numeric"
                    id={id}
                    name={name}
                    value={inputValue}
                    autoComplete="off"
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleKeyDown}
                    placeholder={placeholder}
                    disabled={disabled}
                    required={required}
                    onClick={() => !disabled && setIsOpen((o) => !o)}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2 bg-white text-gray-700 tabular-nums transition-all duration-200 ${disabled
                        ? "bg-gray-100 cursor-not-allowed"
                        : "cursor-pointer hover:border-[#84B044]"
                        } ${hasBlurred && !isValid
                            ? "border-red-500 focus:ring-red-500"
                            : isOpen
                                ? "border-[#84B044] ring-2 ring-[#84B044] focus:ring-[#84B044]"
                                : "border-gray-300 focus:ring-[#84B044]"
                        }`}
                />
                <div
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
                    aria-hidden
                >
                    <Clock
                        className={`h-5 w-5 transition-colors duration-200 ${isOpen ? "text-[#84B044]" : "text-gray-400"
                            }`}
                    />
                </div>
            </div>

            {hasBlurred && !isValid && (
                <p className="mt-1 text-sm text-red-500">{invalidMsg}</p>
            )}

            {isOpen &&
                !disabled &&
                dropdownRect &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        ref={panelRef}
                        className="bg-white rounded-lg shadow-xl border border-gray-200 p-3 sm:p-4 w-full sm:w-auto transform transition-all duration-200 ease-out"
                        style={{
                            position: "fixed",
                            top: dropdownRect.top,
                            left: dropdownRect.left,
                            minWidth: dropdownRect.minWidth,
                            maxWidth: "calc(100vw - 16px)",
                            zIndex: 999,
                        }}
                        role="dialog"
                        aria-label={t("label_hora") || "Hora"}
                    >
                        <p className="text-center text-sm font-semibold text-gray-800 mb-3 tabular-nums">
                            {formatTimeParts(
                                {
                                    hour: pickerHour,
                                    minute: pickerMinute,
                                    second: pickerSecond,
                                },
                                includeSeconds
                            )}
                        </p>

                        <div
                            className={`grid gap-3 ${includeSeconds ? "grid-cols-3" : "grid-cols-2"}`}
                        >
                            <div className="min-w-0 flex flex-col">
                                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 text-center">
                                    {t("label_hora")}
                                </span>
                                <div className="flex-1 max-h-36 sm:max-h-44 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 p-1 grid grid-cols-3 sm:grid-cols-4 gap-1">
                                    {HOURS.map((h) => (
                                        <button
                                            key={h}
                                            type="button"
                                            disabled={isHourDisabled(h)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setPickerHour(h)}
                                            className={`${pickerBtnClass(pickerHour === h)} ${isHourDisabled(h)
                                                ? "opacity-40 cursor-not-allowed"
                                                : ""
                                                }`}
                                        >
                                            {String(h).padStart(2, "0")}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="min-w-0 flex flex-col">
                                <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 text-center">
                                    {labelMinutos}
                                </span>
                                <div className="flex-1 max-h-36 sm:max-h-44 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 p-1 grid grid-cols-3 sm:grid-cols-4 gap-1">
                                    {minuteOptions.map((m) => (
                                        <button
                                            key={m}
                                            type="button"
                                            disabled={isMinuteDisabled(m)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => setPickerMinute(m)}
                                            className={`${pickerBtnClass(pickerMinute === m)} ${isMinuteDisabled(m)
                                                ? "opacity-40 cursor-not-allowed"
                                                : ""
                                                }`}
                                        >
                                            {String(m).padStart(2, "0")}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {includeSeconds && (
                                <div className="min-w-0 flex flex-col">
                                    <span className="text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5 text-center">
                                        {labelSegundos}
                                    </span>
                                    <div className="flex-1 max-h-36 sm:max-h-44 overflow-y-auto overscroll-contain rounded-lg border border-gray-200 p-1 grid grid-cols-3 sm:grid-cols-4 gap-1">
                                        {Array.from({ length: 60 }, (_, s) => s).map((s) => (
                                            <button
                                                key={s}
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => setPickerSecond(s)}
                                                className={pickerBtnClass(pickerSecond === s)}
                                            >
                                                {String(s).padStart(2, "0")}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="mt-3 pt-3 border-t border-gray-200 flex flex-col gap-2 sm:flex-row">
                            <button
                                type="button"
                                onClick={handleNow}
                                className="flex-1 px-3 py-2.5 sm:py-2 text-sm font-medium text-[#84B044] bg-green-50 rounded-lg hover:bg-green-100 transition-colors touch-manipulation"
                            >
                                {t("label_now")}
                            </button>
                            <button
                                type="button"
                                onClick={handlePickerApply}
                                className="flex-1 px-3 py-2.5 sm:py-2 text-sm font-medium text-white bg-[#84B044] rounded-lg hover:bg-[#6f9338] transition-colors touch-manipulation"
                            >
                                {t("label_aceptar")}
                            </button>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
